import { apiConfig } from '../config/api';
import { 
  SurveyExecution, 
  SurveyExecutionRequest, 
  ExternalSurveyRequest, 
  ExternalSurveyInteraction 
} from '../types/survey';

class SurveyService {
  /**
   * Execute a survey with the given parameters
   */
  async executeSurvey(
    request: SurveyExecutionRequest,
    token: string
  ): Promise<SurveyExecution> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.surveyExecute}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to execute survey');
    }

    return await response.json();
  }

  /**
   * Get the status of a survey execution
   */
  async getExecutionStatus(
    executionId: string,
    token: string
  ): Promise<SurveyExecution> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.surveyStatus(executionId)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get execution status');
    }

    return await response.json();
  }

  /**
   * Initiate an external survey interaction
   */
  async initiateExternalSurvey(
    request: ExternalSurveyRequest,
    token: string
  ): Promise<ExternalSurveyInteraction> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.externalSurveyInitiate}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to initiate external survey');
    }

    return await response.json();
  }

  /**
   * Poll the status of an external survey interaction
   */
  async pollExternalSurvey(
    interactionId: string,
    token: string
  ): Promise<ExternalSurveyInteraction> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.externalSurveyPoll(interactionId)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to poll external survey status');
    }

    return await response.json();
  }
}

export const surveyService = new SurveyService();
export default SurveyService;

